/**
 * GSC DETERMINISM CONTRACT - V1.1 (AUDITED)
 * Authority: Senior Software Architect / Reliability Engineer
 * 
 * NOTE DE CONFORMITÉ :
 * Ce contrat définit la capacité du système à produire des résultats identiques 
 * pour des entrées identiques. Il distingue la reproductibilité algorithmique 
 * de la variance matérielle.
 */

export type DeterminismScope = 
    | "bit_exact"      // Identité binaire totale (Rare en DSP browser)
    | "statistically_stable" // Variance < 0.001% (Cible GSC)
    | "context_dependent";   // Dépend du matériel/navigateur

export const DETERMINISM_POLICY = {
    /**
     * @scope Reproductibilité des métriques calculées sur un même environnement.
     * @guarantee Pour un même hash audio, version d'engine et niveau, le score est invariant.
     */
    deterministic_scope: "statistically_stable" as DeterminismScope,

    /**
     * @sources_of_variance
     * 1. Hardware Clock Jitter : La fréquence d'échantillonnage réelle (ex: 44100.02 Hz).
     * 2. Browser FFT implementation : Différences d'optimisation SIMD entre Chrome et Firefox.
     * 3. Floating Point Precision : Accumulation d'erreurs sur des prises longues (> 2min).
     */
    known_sources_of_variance: [
        "sampling_drift",
        "browser_fft_math",
        "float32_rounding_accumulation"
    ],

    /**
     * @enforcement_level HARD
     * Le système bloque activement l'élève en cas de dérive algorithmique pour garantir l'intégrité.
     */
    enforcement_level: "hard" as "hard" | "soft" | "observational"
};

export interface DeterminismAuditMetadata {
    is_stable: boolean;
    drift_detected: boolean;
    engine_parity: boolean;
    ruleset_consistency: boolean;
}